import React from "react";
import { tokens } from "../layout/theme";
import { emphasize, styled } from "@mui/material/styles";
import { Chip, Theme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

/**
 * Interface defining the props for the StyledLinkBtn component.
 * @property {string} label - The text label to display on the button
 * @property {React.ReactElement} [icon] - Optional icon to display before the label
 * @property {(event: React.MouseEvent) => void} [onClick] - Optional click handler function
 * @property {string} path - The target path for navigation
 * @property {boolean} [disabled] - When true, renders a non-clickable grey chip (e.g. role restrictions)
 */
interface StyledLinkBtnProps {
	label: string;
	icon?: React.ReactElement;
	onClick?: (event: React.MouseEvent) => void;
	path: string;
	disabled?: boolean;
}

/**
 * A styled Material-UI Chip component with custom theme-based styling.
 * 
 * This styled component provides consistent styling for navigation buttons,
 * including hover and active states, with colors derived from the application theme.
 * 
 * @param {Theme} theme - The Material-UI theme object
 * @returns {Object} Styled component configuration
 */
const StyledChip = styled(Chip)(({ theme }: { theme: Theme }) => {
	const colors = tokens(theme.palette.mode);
	const backgroundColor = colors.grey[800];
	return {
		backgroundColor,
		height: theme.spacing(3),
		color: theme.palette.text.primary,
		fontWeight: theme.typography.fontWeightRegular,
		fontSize: "1rem",
		marginBottom: ".5rem",
		"&:hover, &:focus": {
			backgroundColor: emphasize(backgroundColor, 0.08),
		},
		"&:active": {
			boxShadow: theme.shadows[1],
			backgroundColor: emphasize(backgroundColor, 0.12),
		},
	};
});

/**
 * A reusable styled link button component for navigation.
 * 
 * This component renders a styled chip that acts as a navigation link.
 * It automatically disables itself when the current path matches the target path,
 * preventing navigation to the current page.
 * 
 * @component
 * @param {StyledLinkBtnProps} props - The props passed to the component
 * @param {string} props.label - The text label to display
 * @param {React.ReactElement} [props.icon] - Optional icon to display
 * @param {Function} [props.onClick] - Optional click handler
 * @param {string} props.path - The target navigation path
 * 
 * @example
 * ```tsx
 * <StyledLinkBtn
 *   label="Home"
 *   icon={<HomeIcon />}
 *   path="/home"
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI Chip component with custom styling
 * - Integrates with React Router for navigation
 * - Automatically disables when on the target path
 * - Supports icon and label combinations
 * - Maintains consistent styling with the application theme
 * - Provides visual feedback through hover and active states
 * 
 * @returns {JSX.Element} A React component that renders a styled navigation button
 */
const StyledLinkBtn: React.FC<StyledLinkBtnProps> = ({
	label,
	icon,
	onClick,
	path,
	disabled: disabledByPolicy,
	...rest
}) => {
	const location = useLocation();
	const currentPath = location.pathname;

	const pathParts = path.split("/") ?? [];
	const pathLastPart = pathParts[pathParts.length - 1];
	const disabledByPath =
		pathLastPart === "." || currentPath.includes(pathLastPart);
	const disabled = Boolean(disabledByPolicy) || disabledByPath;

	if (disabled) {
		return <StyledChip label={label} disabled icon={icon} {...rest} />;
	}

	return (
		<Link to={path} style={{ textDecoration: "none" }}>
			<StyledChip label={label} icon={icon} {...rest} />
		</Link>
	);
};

export default StyledLinkBtn;
